import React, { useState, useContext } from 'react';
import './report-container.scss';
import UploadModalContent from '../modal/upload-modal/upload-modal';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import ReportsService from '../../../../../../services/reports-service';
import PublishReportModal from '../modal/publish-report-modal/publish-report-modal';
import RejectReportModal from '../modal/reject-report-modal/reject-report-modal';
import PdfPreviewModal from '../modal/pdf-preview-modal/pdf-preview-modal';
import FinalPublishSuccessModal from '../modal/final-publish-success-modal/final-publish-success-modal';
import { IdentityContext } from '../../../../../../context/identity-context';
import CommentModal from '../modal/comment-modal/comment-modal';

const ReportContainer = ({ 
  filteredReports = [], 
  handleCheckboxChange, 
  selectedReports
}) => {
  // Local states for table and search
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [searchTerm, setSearchTerm] = useState('');

  // Modal-related state
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showPdfPreviewModal, setShowPdfPreviewModal] = useState(false);
  const [isPublishSuccess, setIsPublishSuccess] = useState(false);
  const [publishSuccessMessage, setPublishSuccessMessage] = useState('');
  const [isRejectSuccess, setIsRejectSuccess] = useState(false);
  const [rejectSuccessMessage, setRejectSuccessMessage] = useState('');
  const [finalPublishSuccessMessage, setFinalPublishSuccessMessage] = useState('');
  const [isFinalPublishSuccess, setIsFinalPublishSuccess] = useState(false);
  const [pdfPreviewUrls, setPdfPreviewUrls] = useState([]);
  const [currentPdfIndex, setCurrentPdfIndex] = useState(0);
  const [previewContinue, setPreviewContinue] = useState(true);
  const [comments, setComments] = useState('');
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedComment, setSelectedComment] = useState('');

  const { user } = useContext(IdentityContext);
  const reportService = new ReportsService();

  // Table actions and sorting
  const handleUploadClick = (report) => {
    setSelectedReport(report);
    setShowUploadModal(true);
  };

  const handleCloseModal = () => {
    setShowUploadModal(false);
    setSelectedReport(null);
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Sort the filteredReports based on sortConfig
  const sortedReports = [...filteredReports].sort((a, b) => {
    if (sortConfig.key) {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'requestedDate' || sortConfig.key === 'dateSubmitted') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
    }
    return 0;
  });

  // Filter reports based on the searchTerm across all columns
  const searchedReports = sortedReports.filter(report => {
    const lowerSearchTerm = searchTerm.toLowerCase();
    const title = (report.reportTitle || "").toString().toLowerCase();
    const issuer = (report.issuer || "").toString().toLowerCase();
    const securityName = (report.securityName || "").toString().toLowerCase();
    const isin = (report.isin || "").toString().toLowerCase();
    const requestedDate = (report.requestedDate || "").toString().toLowerCase();
    const status = (report.status || "").toString().toLowerCase();

    return (
      title.includes(lowerSearchTerm) ||
      issuer.includes(lowerSearchTerm) ||
      securityName.includes(lowerSearchTerm) ||
      isin.includes(lowerSearchTerm) ||
      requestedDate.includes(lowerSearchTerm) ||
      status.includes(lowerSearchTerm)
    );
  });

  const handlePreviewClick = async (report) => {
    try {
      const response = await reportService.retrieveReport(
        report.reportType, 
        report.isin, 
        report.reportTitle, 
        report.requestedDate
      );
      const newWindow = window.open(response.fileURL, '_blank', 'noopener,noreferrer');
      if (newWindow) newWindow.opener = null;
    } catch (error) {
      console.error('Error fetching report preview:', error.message);
    }
  };

  const handleDownloadClick = async (report) => {
    try {
      const response = await reportService.retrieveReport(
        report.reportType, 
        report.isin, 
        report.reportTitle, 
        report.requestedDate
      );
      const fileResponse = await fetch(response.fileURL);
      if (!fileResponse.ok) throw new Error('Network response was not ok');

      const blob = await fileResponse.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const fileName = response.fileName || 'download.pdf';
      link.href = objectUrl;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Error downloading report:', error.message);
    }
  };

  // Enable publish/reject actions only if at least one report is selected
  const areReportsSelected = sortedReports.some(report => selectedReports.has(report.id));

  // Modal handlers
  const handlePublish = () => setShowPublishModal(true);
  const handleReject = () => setShowRejectModal(true);

  const handleCancel = () => {
    setShowPublishModal(false);
    setShowRejectModal(false);
    setShowPdfPreviewModal(false);
    setComments('');
    setPublishSuccessMessage('');
    setRejectSuccessMessage('');
    setIsPublishSuccess(false);
    setIsRejectSuccess(false);
  };

  const handlePublishContinue = async () => {
    const checkerName = user.attributes["custom:display_name"];
    let allLoaded = true;
    try {
      const pdfUrls = [];
      for (const reportId of Array.from(selectedReports)) {
        const report = filteredReports.find((item) => item.id === reportId);
        if (report) {
          const { fileURL } = await reportService.retrieveReport(
            report.reportType, 
            report.isin, 
            report.reportTitle, 
            report.requestedDate
          );
          pdfUrls.push({ url: fileURL, title: report.reportTitle });
        } else {
          allLoaded = false;
        }
      }
      if (allLoaded) {
        setPdfPreviewUrls(pdfUrls);
        setShowPdfPreviewModal(true);
        setShowPublishModal(false);
      } else {
        setPublishSuccessMessage("Some reports failed to load.");
      }
    } catch (error) {
      console.error("Error while fetching report PDFs:", error);
      setPublishSuccessMessage("Some reports failed to load.");
      setIsPublishSuccess(false);
    }
  };

  const handleFinalPublish = async () => {
    const checkerName = user.attributes["custom:display_name"];
    try {
      for (const reportId of Array.from(selectedReports)) {
        const report = filteredReports.find((item) => item.id === reportId);
        if (report) {
          await reportService.checkerSendReport(
            report.reportType, 
            report.reportTitle, 
            report.isin, 
            report.requestedDate, 
            checkerName
          );
        }
      }
      setIsFinalPublishSuccess(true);
      setFinalPublishSuccessMessage("Reports successfully published!");
      setShowPdfPreviewModal(false);
    } catch (error) {
      console.error("Error while publishing reports:", error);
      setIsFinalPublishSuccess(false);
      setFinalPublishSuccessMessage("Failed to publish reports.");
      setShowPdfPreviewModal(false);
    }
    setPreviewContinue(false);
  };

  const handleRejectContinue = async () => {
    const checkerName = user.attributes["custom:display_name"];
    try {
      for (const reportId of Array.from(selectedReports)) {
        const report = filteredReports.find((item) => item.id === reportId);
        if (report) {
          await reportService.checkerRejectReport(
            report.reportType, 
            report.reportTitle, 
            report.isin, 
            report.requestedDate, 
            checkerName, 
            comments
          );
        }
      }
      setIsRejectSuccess(true);
      setRejectSuccessMessage("Reports successfully rejected!");
    } catch (error) {
      console.error("Error while rejecting reports:", error);
    }
  };

  const handleCommentClick = (comment) => {
    setSelectedComment(comment);
    setShowCommentModal(true);
  };

  return (
    <Container className="reports-container">
      <Row>
        <Col xs={12}>
          <div className="search-container">
            <Form.Control
              type="text"
              placeholder="Search reports..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-box"
            />
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <div className="reports-table-container">
            <div className="table-container">
              <table className="report-table">
                <thead>
                  <tr>
                    <th className="checkbox-header"></th>
                    <th>Report Title</th>
                    <th>Issuer</th>
                    <th>Security Name</th>
                    <th>ISIN</th>
                    <th onClick={() => handleSort('requestedDate')}>
                      Date of Request {sortConfig.key === 'requestedDate' && (sortConfig.direction === 'ascending' ? '▲' : '▼')}
                    </th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {searchedReports.map(report => (
                    <tr key={report.id}>
                      <td className="checkbox-cell">
                        {report.status === 'For Checking' ? (
                          <input 
                            type="checkbox" 
                            checked={selectedReports.has(report.id)} 
                            onChange={() => handleCheckboxChange(report.id)} 
                          />
                        ) : null}
                      </td>
                      <td>{report.reportTitle}</td>
                      <td>{report.issuer}</td>
                      <td>{report.securityName}</td>
                      <td>{report.isin}</td>
                      <td>{report.requestedDate}</td>
                      <td className={`status ${report.status.replace(' ', '-').toLowerCase()}`}>
                        {report.status}
                        {report.comment && (
                          <span
                            style={{ cursor: 'pointer', marginLeft: '5px' }}
                            onClick={() => handleCommentClick(report.comment)}
                          >
                            &#9865;
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <Button variant="primary" className="action-button" onClick={() => handlePreviewClick(report)}>
                            Preview
                          </Button>
                          <Button variant="primary" className="action-button" onClick={() => handleDownloadClick(report)}>
                            Download
                          </Button>
                          {report.status === 'For Checking' && (
                            <Button variant="primary" className="action-button" onClick={() => handleUploadClick(report)}>
                              Upload
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <div className="actions-container">
            <Button onClick={handleReject} className="reject-button" disabled={!areReportsSelected}>
              Reject Report
            </Button>
            <Button onClick={handlePublish} className="publish-button" variant="primary" disabled={!areReportsSelected}>
              Publish Report to Issuer
            </Button>
          </div>
        </Col>
      </Row>
      {selectedReport && (
        <Modal show={showUploadModal} onHide={handleCloseModal} dialogClassName="checker-upload-modal">
          <Modal.Header closeButton>
            <Modal.Title>Upload Document</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <UploadModalContent context={{ report: selectedReport }} onClose={handleCloseModal} />
          </Modal.Body>
        </Modal>
      )}
      <PublishReportModal
        show={showPublishModal}
        isPublishSuccess={isPublishSuccess}
        publishSuccessMessage={publishSuccessMessage}
        handleCancel={handleCancel}
        handlePublishContinue={handlePublishContinue}
      />
      <RejectReportModal
        show={showRejectModal}
        isRejectSuccess={isRejectSuccess}
        rejectSuccessMessage={rejectSuccessMessage}
        comments={comments}
        setComments={setComments}
        handleCancel={handleCancel}
        handleRejectContinue={handleRejectContinue}
      />
      <PdfPreviewModal
        show={showPdfPreviewModal}
        pdfPreviewUrls={pdfPreviewUrls}
        currentPdfIndex={currentPdfIndex}
        setCurrentPdfIndex={setCurrentPdfIndex}
        handleCancel={handleCancel}
        handleFinalPublish={handleFinalPublish}
      />
      <FinalPublishSuccessModal
        show={isFinalPublishSuccess}
        onHide={() => setIsFinalPublishSuccess(false)}
        finalPublishSuccessMessage={finalPublishSuccessMessage}
      />

      <CommentModal
        show={showCommentModal}
        comment={selectedComment}
        onClose={() => setShowCommentModal(false)}
      />
    </Container>
  );
};

export default ReportContainer;
